import { api, REPORT_RUN_TIMEOUT_MS } from './client'
import { requireArray, requireArrayIn } from './readShape'
import { specializedJobsOn, runReportAsJob } from './reportJobRun'
import { responseStatus } from './upstreamStatus'

/**
 * The CSV-shaped reports return `{ headers, rows, meta }` rather than a bare array, and
 * the old fallback was `data?.data || { headers: [], rows: [], meta: [] }` — the same
 * bug wearing a different shape, since an unreadable body produced an empty REPORT.
 *
 * `headers` and `rows` are both required to be arrays because the renderers index into
 * them directly; `meta` is optional and normalised, because several of these endpoints
 * legitimately omit it. A report with headers and no rows is a real, empty report and
 * passes through untouched.
 */
function requireCsvShape(payload, path) {
  const noun = `${path} report`
  const body = payload?.data
  if (!body || typeof body !== 'object') {
    throw new Error(
      `The ${noun} came back in a shape we couldn't read. ` +
      'This is not an empty report — try again, and report it if it persists.'
    )
  }
  requireArray(body.headers, { noun: `${noun} columns`, emptyNoun: 'report' })
  requireArray(body.rows, { noun, emptyNoun: 'report' })
  return { headers: body.headers, rows: body.rows, meta: Array.isArray(body.meta) ? body.meta : [] }
}

/**
 * Shared client for "service-method" specialized reports — Time Entries,
 * Billing Realization, Calendar, etc. Each one POSTs a filter object and
 * gets back an array of rows. Exports of the JSON service-method reports are
 * streamed through the generic /reports/0/export.{csv,xlsx} endpoint with a
 * `legacyReportType` discriminator so CB's controller knows which view model
 * to materialize.
 *
 * Spec 200 — the CSV-path reports' exports are their OWN route instead,
 * `POST /reports/{path}/export.{csv,xlsx}` with the run's filter as the body:
 * cb-api builds the file's CB request with the very function its run uses
 * (tenant field ids injected, the exact type string, which now lives only in
 * cb-api's `LEGACY_CSV_REPORTS`), behind the run's own gate. The generic route's
 * gate asked about report 0, which no tab holds.
 */

const buildExportUrl = (filter, format, legacyReportType) => {
  const params = new URLSearchParams()
  // Reports/DownloadCSV expects the filter as a JSON-stringified object
  // (NOT the wrapped {filters:[...]} we use for the generic engine).
  params.set('filters', JSON.stringify(filter))
  params.set('legacyReportType', legacyReportType)
  return `/api/v1/reports/0/export.${format}?${params.toString()}`
}

const downloadFileFromUrl = async (url, getToken, fallbackName, init = {}) => {
  const token = await getToken()
  const resp = await fetch(url, { ...init, headers: { ...init.headers, Authorization: `Bearer ${token}` } })
  if (!resp.ok) throw new Error(`Export failed: ${responseStatus(resp)}`)
  const blob = await resp.blob()
  const dispo = resp.headers.get('content-disposition') || ''
  const m = /filename="?([^"]+)"?/.exec(dispo)
  const filename = m?.[1] || fallbackName
  const objUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(objUrl)
}

// Route a specialized report run through the async job system (spec 055): the server
// replays this report's own POST endpoint off the request path. Same { data: … } shape.
async function runViaSpecializedJob(path, filter, opts) {
  return runReportAsJob({
    kind: 'specialized',
    actionKey: opts.actionKey || path,
    reportId: opts.reportId,
    endpoint: { method: 'POST', path: `/reports/${path}`, body: filter },
  })
}

/**
 * Spec 200 — the file formats a queued or scheduled run of the specialized report at
 * `path` (its run path, as the page's `queue.endpoint` names it) can be emailed in.
 * cb-api's mailer attaches the report's own export route for these, and nothing else
 * (cb-api `jobs/specializedExport.js` `ATTACHABLE_POST_EXPORTS`, which a test keeps this
 * table equal to). Spec 202 adds Revenue & Commissions (its CSV file now gets the run's
 * own percent correction, CC-50049) and A/P DETAIL (summary has no file). Spec 206 adds
 * Bill Journal and Fee Allocation, whose runs are legacy's CSV since spec 203. Spec 207
 * adds Aged A/R, Excel only (CB's A/R CSV writes non-ASCII as `?`). Any other path: none,
 * a link only.
 */
const ATTACH_FORMATS = Object.freeze({
  '/reports/time-entries': Object.freeze(['xlsx', 'csv']),
  '/reports/timekeeper-analysis': Object.freeze(['xlsx', 'csv']),
  '/reports/monthly-productivity': Object.freeze(['xlsx', 'csv']),
  '/reports/fee-receipts': Object.freeze(['xlsx', 'csv']),
  '/reports/revenue-commissions': Object.freeze(['xlsx', 'csv']),
  '/reports/ap/detail': Object.freeze(['xlsx', 'csv']),
  '/reports/bill-journal': Object.freeze(['xlsx', 'csv']),
  '/reports/fee-allocation': Object.freeze(['xlsx', 'csv']),
  '/reports/ar/run-all': Object.freeze(['xlsx']),
})
const NO_FORMATS = Object.freeze([])
export const specializedAttachFormats = (path) => (Object.prototype.hasOwnProperty.call(ATTACH_FORMATS, path) ? ATTACH_FORMATS[path] : NO_FORMATS)
export const _attachFormatsTable = ATTACH_FORMATS

/** A CSV-path report's own export: `POST /reports/{path}/export.{format}`, the run's filter as the body. */
const ownExportRequest = (path, filter, format) => ({
  url: `/api/v1/reports/${path}/export.${format}`,
  init: { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(filter) },
})

const buildReport = (path, legacyReportType, opts = {}) => ({
  /**
   * POST /reports/{path} — runs the report and returns the response shape.
   * Reports on the JSON service-method path: { rows: object[] }
   * Reports on the CSV-export path:          { headers, rows, meta }
   */
  async run(filter, runOpts = {}) {
    if (specializedJobsOn()) {
      // runOpts.reportId is carried on the job so the "My Reports" tray can link
      // back to /reports/:id and the component can reopen the saved result.
      const result = await runViaSpecializedJob(path, filter, { ...opts, reportId: runOpts.reportId })
      if (opts.csvShape) return requireCsvShape(result, path)
      return requireArrayIn(result, 'rows', { noun: `${path} report`, emptyNoun: 'report' })
    }
    // CC-50022: report runs ride the extended timeout, not the global 30s.
    const data = await api.post(`/reports/${path}`, filter, { timeout: REPORT_RUN_TIMEOUT_MS })
    if (opts.csvShape) {
      return requireCsvShape(data, path)
    }
    // CC-53052 — this was `data?.data?.rows || []`, and it is the worst place in the
    // app for that expression. These are A/R, Time Entries, Fee Receipts, Bill Journal:
    // a 200 whose body we cannot read rendered a report with ZERO ROWS and no error,
    // which in a billing application reads as "nothing owed" rather than "the read
    // failed". Same family as CC-50321 / CC-53047, higher consequence than either.
    //
    // A genuinely empty report still returns [] — a firm with nothing outstanding is a
    // real answer, and the guard is only about SHAPE.
    return requireArrayIn(data, 'rows', { noun: `${path} report`, emptyNoun: 'report' })
  },
  /**
   * Build a full URL for the CSV/XLSX download — the generic GET route, so only for a
   * report that downloads through it (a CSV-path report's export is a POST, spec 200).
   */
  exportUrl(filter, format) {
    return legacyReportType ? buildExportUrl(filter, format, legacyReportType) : null
  },
  /** Trigger a browser download */
  async download(filter, format, getToken, fallbackName) {
    const name = fallbackName || `${path}.${format}`
    if (opts.csvShape) {
      const own = ownExportRequest(path, filter, format)
      return downloadFileFromUrl(own.url, getToken, name, own.init)
    }
    return downloadFileFromUrl(buildExportUrl(filter, format, legacyReportType), getToken, name)
  },
})

const specializedReportService = {
  // CSV-export path (cb-api parses /web/Reports/DownloadCSV server-side
  // and returns { headers, rows, meta }). Use opts.csvShape to indicate.
  // Spec 200: their `legacyReportType` lives only in cb-api now (`LEGACY_CSV_REPORTS`,
  // routes.js) — the run AND the export are cb-api's own routes, so cb2 never names it.
  // CC-50020's exact strings, and spec 203's Fee Allocation one, are pinned there.
  timeEntries: buildReport('time-entries', null, { csvShape: true }),
  revenueCommissions: buildReport('revenue-commissions', null, { csvShape: true }),
  timekeeperAnalysis: buildReport('timekeeper-analysis', null, { csvShape: true }),
  monthlyProductivity: buildReport('monthly-productivity', null, { csvShape: true }),
  feeReceipts: buildReport('fee-receipts', null, { csvShape: true }),
  // Spec 203: Bill Journal and Fee Allocation run on legacy's own CSV too. Their
  // dispatcher rows were raw lines that only legacy's view model organizes (Fee
  // Allocation's summed 12x too high), and the runs had never worked.
  billJournal: buildReport('bill-journal', null, { csvShape: true }),
  feeAllocation: buildReport('fee-allocation', null, { csvShape: true }),
  // JSON service-method path — typed-rows array. cb-api sends the filter as CB's
  // DataContract class (spec 203) and answers with ISO dates.
  billingRealization: buildReport('billing-realization', 'Models.BillingRealizationReportViewModel'),
  calendar: buildReport('calendar', 'Models.CalendarReportViewModel'),
  // No legacy file exists for this report (no TranslateToReport), so it has no Export.
  paymentsByCase: buildReport('payments-by-case', 'Models.PaymentsByCaseReportViewModel'),
  daily: {
    async run(filter) {
      const data = await api.post('/reports/daily', filter, { timeout: REPORT_RUN_TIMEOUT_MS })
      return data?.data || null
    },
  },
}

export default specializedReportService
